<script setup lang="ts">
  import { computed, ref } from 'vue';
  import type { Component } from 'vue';

  import type { EmailThread, Partner, Task } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import { Card, CardContent, CardHeader, CardTitle } from '@ui/components/card';
  import { Tabs, TabsList, TabsTrigger } from '@ui/components/tabs';
  import { ToggleGroup, ToggleGroupItem } from '@ui/components/toggle-group';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import TaskItem from '@/components/common/TaskItem.vue';
  import CreditMemoCompact from '@/components/tasks/CreditMemoCompact.vue';
  import InvoiceCompact from '@/components/tasks/InvoiceCompact.vue';
  import TaskReplyWarning from '@/components/tasks/TaskReplyWarning.vue';

  interface Props {
    emailThread: EmailThread;
    partner?: Partner;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    (e: 'open-task', task: Task): void;
    (e: 'before-task-open'): void;
  }>();

  const emailThreadTasks = computed(() => props.emailThread?.tasks);

  const openEmailThreadTasks = computed(() =>
    emailThreadTasks.value?.filter((task) => task?.isOpen),
  );

  const completedEmailThreadTasks = computed(() =>
    emailThreadTasks.value?.filter((task) => task?.isCompleted),
  );

  const closedEmailThreadTasks = computed(() =>
    emailThreadTasks.value?.filter((task) => task?.isClosed),
  );

  const currentTaskType = ref<'open' | 'completed' | 'closed'>('open');

  const filteredTasks = computed(() => {
    switch (currentTaskType.value) {
      case 'closed':
        return closedEmailThreadTasks.value;
      case 'completed':
        return completedEmailThreadTasks.value;
      case 'open':
      default:
        return openEmailThreadTasks.value;
    }
  });

  const hasTasks = computed(() => !!emailThreadTasks.value?.length);
  const hasInvoices = computed(() => !!props.emailThread?.invoices?.length);
  const hasCreditMemos = computed(() => !!props.emailThread?.credit_memos?.length);

  type SidebarView = 'tasks' | 'invoices' | 'credit_memos';

  const sidebarViews = computed<{ key: SidebarView; label: string; icon: string; has: boolean }[]>(
    () => [
      { key: 'tasks', label: 'Tasks', icon: 'far fa-list-check', has: hasTasks.value },
      {
        key: 'invoices',
        label: 'Invoices',
        icon: 'far fa-file-invoice-dollar',
        has: hasInvoices.value,
      },
      {
        key: 'credit_memos',
        label: 'Credit memos',
        icon: 'far fa-file-plus-minus',
        has: hasCreditMemos.value,
      },
    ],
  );

  const availableViews = computed(() => sidebarViews.value.filter((view) => view.has));

  const sidebarView = ref<SidebarView>('tasks');

  // Show the selected view when it has content; otherwise fall back to the first that does.
  const displayedView = computed<SidebarView>(() => {
    const views = availableViews.value;
    if (views.some((view) => view.key === sidebarView.value)) return sidebarView.value;
    return views[0]?.key ?? 'tasks';
  });

  const activeView = computed(() =>
    sidebarViews.value.find((view) => view.key === displayedView.value),
  );

  // Invoices and credit memos render identically — only the component, prop name, and source differ.
  interface DocumentRenderer {
    component: Component;
    items: () => { id: string }[];
    propName: string;
  }

  const documentRenderers: Record<'invoices' | 'credit_memos', DocumentRenderer> = {
    invoices: {
      component: InvoiceCompact,
      propName: 'invoice',
      items: () => props.emailThread?.invoices ?? [],
    },
    credit_memos: {
      component: CreditMemoCompact,
      propName: 'creditMemo',
      items: () => props.emailThread?.credit_memos ?? [],
    },
  };

  const activeDocumentRenderer = computed(() =>
    displayedView.value === 'tasks' ? undefined : documentRenderers[displayedView.value],
  );
</script>

<template>
  <Card class="lg:w-110 shrink-0 overflow-hidden lg:max-h-fit px-0 pb-0">
    <CardHeader class="flex-col items-start w-full shrink-0 gap-y-6 lg:w-110 lg:min-w-110">
      <CardTitle>{{ activeView?.label }}</CardTitle>

      <ToggleGroup
        v-if="availableViews.length > 1"
        :model-value="displayedView"
        type="single"
        variant="outline"
        size="sm"
        class="w-full shrink-0"
        @update:model-value="(v) => v && (sidebarView = v as SidebarView)"
      >
        <ToggleGroupItem
          v-for="view in availableViews"
          :key="view.key"
          :value="view.key"
          :aria-label="view.label"
          class="flex-1 data-[state=on]:bg-secondary data-[state=on]:text-secondary-foreground"
        >
          <Tooltip>
            <TooltipTrigger as-child>
              <i :class="view.icon" />
            </TooltipTrigger>
            <TooltipContent>{{ view.label }}</TooltipContent>
          </Tooltip>
        </ToggleGroupItem>
      </ToggleGroup>
    </CardHeader>

    <CardContent
      class="space-y-4 w-full lg:w-110 lg:min-w-110 lg:sticky lg:top-0 pb-10 overflow-y-auto"
    >
      <template v-if="displayedView === 'tasks'">
        <Tabs v-model="currentTaskType">
          <TabsList no-border>
            <TabsTrigger value="open">
              Open
              <span v-if="openEmailThreadTasks?.length" class="font-light">
                {{ openEmailThreadTasks?.length }}
              </span>
            </TabsTrigger>

            <TabsTrigger value="completed">
              Completed
              <span v-if="completedEmailThreadTasks?.length" class="font-light">
                {{ completedEmailThreadTasks?.length }}
              </span>
            </TabsTrigger>

            <TabsTrigger value="closed">
              Closed
              <span v-if="closedEmailThreadTasks?.length" class="font-light">
                {{ closedEmailThreadTasks?.length }}
              </span>
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div v-if="filteredTasks?.length" class="space-y-3.5">
          <Button
            v-for="task in filteredTasks"
            :key="task?.id"
            variant="outline"
            class="w-full h-10 px-0 gap-x-0 group"
            @click="emit('open-task', task as Task)"
          >
            <TaskItem
              v-if="task"
              class="px-2.5 hover:bg-transparent [&>div]:h-full h-full rounded"
              size="2xs"
              :task="task"
              :show-customer="false"
              :truncate="true"
              @before-task-open="emit('before-task-open')"
            >
              <template #task-item-content-right>
                <div class="flex items-center gap-x-1.5 ml-2">
                  <TaskReplyWarning
                    class="text-3xs font-bold"
                    icon-class-name="text-3xs!"
                    gap-class-name="0.5"
                    :task="task"
                    :compact="true"
                  />
                </div>
              </template>
            </TaskItem>

            <Tooltip :delay-duration="0">
              <TooltipTrigger as-child>
                <div
                  class="flex items-center gap-x-2 h-full rounded-tr rounded-br border-l px-2.5 duration-300 transition-all"
                  :class="{
                    'bg-secondary group-hover:bg-secondary/60':
                      task?.isReplyRequired && !task?.isOpen,
                  }"
                >
                  <i
                    class="fa far text-2xs!"
                    :class="{
                      'fa-reply text-agent': task?.isReplyRequired && task?.isOpen,
                      'fa-check text-success': task?.isReplyRequired && !task?.isOpen,
                      'fa-arrow-right text-muted-foreground': !task?.isReplyRequired,
                    }"
                  />
                </div>
              </TooltipTrigger>

              <TooltipContent side="top"> View task </TooltipContent>
            </Tooltip>
          </Button>
        </div>

        <div v-else class="h-12 flex items-center border px-5 text-xs rounded">
          <p class="text-muted-foreground text-xs">No {{ currentTaskType }} tasks</p>
        </div>
      </template>

      <div v-else-if="activeDocumentRenderer" class="flex flex-col gap-y-4 w-full">
        <component
          :is="activeDocumentRenderer.component"
          v-for="item in activeDocumentRenderer.items()"
          :key="item.id"
          v-bind="{
            [activeDocumentRenderer.propName]: item,
            customer: partner,
            showCustomer: false,
          }"
        />
      </div>
    </CardContent>
  </Card>
</template>
